import { useUpdateURLSearchParams } from '@/hooks/useConvertToURLSearchParams';
import type {
  AdminInvoiceReportsSearchQuery,
  FetchAdminInvoiceReportsArgs,
  OrderByItem,
} from '@/features/admin_invoice_reports/types';
import {
  InvoiceReportOrderByDirection,
  InvoiceReportOrderByField,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import { buildOrderByArray } from '@/features/admin_invoice_reports/utils/buildOrderByArray';
import { isEqual } from '@/utils/isEqual';
import { useCallback, useMemo, useState } from 'react';
import type { MultiColumnSortState } from '../Table/types';

const DEFAULT_SORT: OrderByItem[] = [
  {
    field: InvoiceReportOrderByField.number,
    direction: InvoiceReportOrderByDirection.desc,
  },
];

type UseHandleSearchQueryResult = {
  fetchArgs: FetchAdminInvoiceReportsArgs;
  searchQuery: AdminInvoiceReportsSearchQuery;
  sortOrders: MultiColumnSortState[];
  onSearch: (
    query: AdminInvoiceReportsSearchQuery,
    refetchTable?: () => void
  ) => void;
  onMultiColumnSort: (sortStates: MultiColumnSortState[]) => void;
};

export const useAdminInvoiceReportsHandleSearchQuery = (
  initialQuery: AdminInvoiceReportsSearchQuery = {}
): UseHandleSearchQueryResult => {
  const [searchQuery, setSearchQuery] =
    useState<AdminInvoiceReportsSearchQuery>(initialQuery);
  const [sortOrders, setSortOrders] = useState<MultiColumnSortState[]>([]);

  const updateSearchParams = useUpdateURLSearchParams();

  const onSearch = useCallback(
    (query: AdminInvoiceReportsSearchQuery, refetchTable?: () => void) => {
      // Page-only: from pagination navigation
      if ('page' in query && Object.keys(query).length === 1) {
        const merged = { ...searchQuery, page: query.page };
        setSearchQuery(merged);
        updateSearchParams(
          Object.fromEntries(
            Object.entries(merged).filter(([, v]) => v !== undefined)
          )
        );
        return;
      }
      // Full search from form button: query has no page (stripped by Search component)
      // Compare criteria only — exclude page from current searchQuery
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { page: _cp, ...currentCriteria } = searchQuery;
      if (isEqual(currentCriteria, query)) {
        refetchTable?.();
        return;
      }
      const newSearchQuery = { ...query, page: 1 };
      setSearchQuery(newSearchQuery);
      updateSearchParams(
        Object.fromEntries(
          Object.entries(newSearchQuery).filter(([, v]) => v !== undefined)
        )
      );
    },
    [updateSearchParams, searchQuery]
  );

  const onMultiColumnSort = useCallback(
    (sortStates: MultiColumnSortState[]) => {
      setSortOrders(sortStates);
    },
    []
  );

  const orderBy = useMemo(() => buildOrderByArray(sortOrders), [sortOrders]);

  const fetchArgs: FetchAdminInvoiceReportsArgs = useMemo(() => {
    // Strip pagination-only fields before sending to the API query body
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, sort_fields, sort_orders, ...apiQuery } = searchQuery;
    return {
      page: page ?? 1,
      perPage: 50,
      query: apiQuery,
      order_by: orderBy ?? DEFAULT_SORT,
    };
  }, [searchQuery, orderBy]);

  return { fetchArgs, searchQuery, sortOrders, onSearch, onMultiColumnSort };
};
